import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Download, Image as ImageIcon, X } from 'lucide-react';
import { PrimaryButton } from '@/components/ui/DesignSystem';
import {
  BANK_CARD_FONT,
  BANK_CARD_LOGO_URL,
  BANK_CARD_TEMPLATES,
  downloadBankCardFile,
  fieldStyle,
  getBankCardData,
  renderBankCard,
} from '@/lib/bankCard';

function AutoFitText({ field, children }) {
  const fieldRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const container = fieldRef.current;
    const text = textRef.current;
    if (!container || !text) return undefined;

    const fit = () => {
      const scale = container.clientWidth / field.width;
      let low = Math.max(8, field.minFontSize * scale);
      let high = Math.max(low, field.fontSize * scale);
      const availableWidth = container.clientWidth * 0.92;
      const availableHeight = container.clientHeight * 0.86;
      for (let index = 0; index < 10; index += 1) {
        const size = (low + high) / 2;
        text.style.fontSize = `${size}px`;
        if (text.scrollWidth <= availableWidth && text.scrollHeight <= availableHeight) low = size;
        else high = size;
      }
      text.style.fontSize = `${low}px`;
    };

    fit();
    document.fonts?.ready.then(fit).catch(() => {});
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [children, field]);

  return (
    <div ref={fieldRef} className="flex h-full w-full items-center justify-center overflow-hidden" dir={field.direction}>
      <span
        ref={textRef}
        className="block max-w-[92%] overflow-hidden text-ellipsis whitespace-nowrap text-center font-extrabold leading-none text-[#F7F2E7]"
        style={{ fontFamily: `"${BANK_CARD_FONT}", Arial, sans-serif` }}
      >
        {children}
      </span>
    </div>
  );
}

function CardPreview({ format, data }) {
  const template = BANK_CARD_TEMPLATES[format];
  const labels = { name: 'اسم صاحب الحساب', usd: 'الرصيد بالدولار', lbp: 'الرصيد بالليرة' };
  return (
    <div
      className="relative w-full overflow-hidden rounded-[clamp(1rem,3vw,2.5rem)] border-[clamp(2px,.55vw,8px)] border-[#D5B477] shadow-xl"
      style={{ aspectRatio: `${template.width} / ${template.height}`, background: 'linear-gradient(145deg,#171A3A 55%,#087F6B)', containerType: 'inline-size' }}
      data-card-format={format}
    >
      <div className="absolute left-[-8%] top-[-16%] h-[40%] w-[52%] rotate-[-20deg] rounded-[50%] border-b border-[#D5B477] bg-[#087F6B]/70" aria-hidden="true" />
      <div className="absolute bottom-[-19%] left-[-15%] h-[32%] w-[125%] rotate-[8deg] rounded-[50%] border-t border-[#D5B477] bg-[#22A98B]/70" aria-hidden="true" />
      <img src={BANK_CARD_LOGO_URL} alt="شعار بنك العائلة الرسمي" className="absolute select-none object-contain" style={fieldStyle(template, template.logo)} draggable="false" />
      <strong className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-extrabold leading-none text-[#D5B477]" style={{ left: `${template.title.x / template.width * 100}%`, top: `${template.title.y / template.height * 100}%`, fontSize: `${template.title.fontSize / template.width * 100}cqw` }}>بنك العائلة</strong>
      {format === 'landscape' && <div className="absolute left-[40%] top-[17%] h-[57%] w-px bg-[#D5B477]/70" aria-hidden="true" />}
      {Object.entries(template.fields).map(([key, field]) => (
        <div key={key} style={fieldStyle(template, field)} className={`absolute rounded-[clamp(.55rem,2vw,1.8rem)] border ${key === 'lbp' ? 'border-[#D5B477]' : 'border-[#22A98B]'} bg-[linear-gradient(125deg,#087F6B,#171A3A)] shadow-lg`}>
          <span className={`absolute inset-x-0 top-[8%] text-center font-bold ${key === 'name' ? 'text-[#D5B477]' : 'text-[#F7F2E7]'}`} style={{ fontSize: `${Math.max(27, Math.round(field.fontSize * 0.5)) / template.width * 100}cqw` }}>{labels[key]}</span>
          <div className="absolute inset-x-0 bottom-0 h-[68%]"><AutoFitText field={field}>{data[key] || '—'}</AutoFitText></div>
        </div>
      ))}
    </div>
  );
}

function DownloadDialog({ busyFormat, error, onClose, onDownload }) {
  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape' && !busyFormat) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busyFormat, onClose]);

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/70 p-4" role="presentation" onMouseDown={() => !busyFormat && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bank-card-download-title"
        className="w-full max-w-md rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-[var(--color-text-primary)] shadow-2xl sm:p-6"
        onMouseDown={event => event.stopPropagation()}
        dir="rtl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id="bank-card-download-title" className="text-lg font-black">اختر شكل البطاقة</h3>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">سيتم إنشاء صورة عالية الجودة من بيانات حسابك الحالية.</p>
          </div>
          <button type="button" onClick={onClose} disabled={Boolean(busyFormat)} aria-label="إغلاق" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl hover:bg-[var(--color-surface-secondary)] disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          {['portrait', 'landscape'].map(format => {
            const template = BANK_CARD_TEMPLATES[format];
            const busy = busyFormat === format;
            return (
              <button
                key={format}
                type="button"
                onClick={() => onDownload(format)}
                disabled={Boolean(busyFormat)}
                className="flex min-h-28 flex-col items-center justify-center gap-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-secondary)] p-3 text-center font-bold transition hover:border-[var(--c-gold)] disabled:opacity-55"
              >
                <ImageIcon className={`h-7 w-7 ${format === 'portrait' ? '' : 'rotate-90'}`} />
                <span>{template.label}</span>
                <span className="font-num text-[11px] text-[var(--color-text-secondary)]">{template.width} × {template.height}</span>
                {busy && <span className="text-xs text-[var(--c-gold)]">جارٍ الإنشاء…</span>}
              </button>
            );
          })}
        </div>
        {error && <p role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{error}</p>}
      </div>
    </div>
  );
}

export default function FamilyBankCard({ me }) {
  const data = useMemo(() => getBankCardData(me), [me]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyFormat, setBusyFormat] = useState('');
  const [error, setError] = useState('');

  const closeDialog = () => {
    if (!busyFormat) {
      setDialogOpen(false);
      setError('');
    }
  };

  const download = async format => {
    setBusyFormat(format);
    setError('');
    try {
      const rendered = await renderBankCard(format, data);
      downloadBankCardFile(rendered, format);
      setDialogOpen(false);
    } catch (downloadError) {
      setError(downloadError?.message || 'تعذر تنزيل البطاقة. حاول مرة أخرى.');
    } finally {
      setBusyFormat('');
    }
  };

  return (
    <section className="mb-brand-card rounded-3xl border bg-[var(--color-surface)] p-3 sm:p-4" dir="rtl" aria-label="بطاقة بنك العائلة الرسمية">
      <div className="mx-auto w-full max-w-[32rem] md:hidden">
        <CardPreview format="portrait" data={data} />
      </div>
      <div className="mx-auto hidden w-full max-w-[72rem] md:block">
        <CardPreview format="landscape" data={data} />
      </div>

      <div className="mt-4 flex justify-center sm:justify-start">
        <PrimaryButton size="sm" onClick={() => setDialogOpen(true)}>
          <Download className="h-4 w-4" />
          تحميل البطاقة
        </PrimaryButton>
      </div>

      {dialogOpen && (
        <DownloadDialog busyFormat={busyFormat} error={error} onClose={closeDialog} onDownload={download} />
      )}
    </section>
  );
}
